import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  Box,
  Button,
  Typography,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { DialogAnimate } from "src/components/Animate";
import {
  closeContractDialog,
  getAddOns,
  getActiveAddOns,
  setAddOnQuantity,
} from "src/redux/slices/addOns";
import { apiErrorText } from "src/utils/apiErrorCode";
import { pickAddOnForCycle, addOnPriceLabel } from "src/views/user/AccountView/Billing/contractAddOnsHelpers";

// F64 · Modal global disparado desde cualquier CTA "Contrátalo" del nav.
// Recibe el NOMBRE del add-on, busca su registro en el catálogo y confirma
// la contratación (setAddOnQuantity=1). Al éxito refresca activeAddOnNames.
export default function AddOnContractDialog() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const contractDialogName = useSelector((s) => s.addOns?.contractDialogName);
  const { addOns } = useSelector((s) => s.addOns);
  // Ciclo de facturación del plan: en planes anuales el extra se cobra
  // anual (gemelo anual) y así se muestra.
  const planIsYearly = useSelector((s) => !!s.authJwt?.user?.plan?.isYearly);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (contractDialogName && (!addOns || addOns.length === 0)) {
      dispatch(getAddOns(true));
    }
  }, [contractDialogName, addOns, dispatch]);

  if (!contractDialogName) return null;

  const target = pickAddOnForCycle(addOns, contractDialogName, planIsYearly ? "yearly" : "monthly");

  const handleClose = () => dispatch(closeContractDialog());

  const handleContract = async () => {
    if (!target) return;
    setSubmitting(true);
    try {
      const res = await dispatch(setAddOnQuantity(target.id, 1));
      // setAddOnQuantity ya espera el refresco forzado de activeAddOnNames
      // (y el backend invalida su caché), así que el nav y los AddOnGate
      // quedan desbloqueados al cerrar el modal, sin recargar.
      await dispatch(getActiveAddOns({ force: true }));
      enqueueSnackbar(
        res?.alreadyActive
          ? `${target.name} ya estaba contratado — activado en este dispositivo`
          : `${target.name} activado correctamente`,
        { variant: res?.alreadyActive ? "info" : "success" },
      );
      handleClose();
    } catch (err) {
      enqueueSnackbar(apiErrorText(err) || "No se pudo activar el add-on", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const priceLabel = target ? addOnPriceLabel(target) : "";
  const isYearlyPrice = target?.billingCycle === "yearly";

  return (
    <DialogAnimate open onClose={handleClose} maxWidth="xs" fullWidth>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
          Contratar {contractDialogName}
        </Typography>

        {!target && (
          <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Cargando información del extra…
            </Typography>
          </Stack>
        )}

        {target && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              {target.description}
            </Alert>
            <Box sx={{
              p: 2, mb: 2, borderRadius: 1, bgcolor: "grey.100",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <Typography variant="body2">
                {isYearlyPrice ? "Precio anual (IVA incl.)" : "Precio mensual (IVA incl.)"}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: "#0d9488" }}>
                {priceLabel}
              </Typography>
            </Box>
            <Stack spacing={0.5} sx={{ mb: 3, color: "text.secondary" }}>
              <Typography variant="body2">
                <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5, color: "success.main", verticalAlign: "middle" }} />
                Hoy se cobra la parte proporcional hasta tu próxima facturación.
              </Typography>
              <Typography variant="body2">
                <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5, color: "success.main", verticalAlign: "middle" }} />
                Puedes cancelarlo cuando quieras — sin permanencia.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={handleClose} disabled={submitting}>Cancelar</Button>
              <LoadingButton
                variant="contained"
                loading={submitting}
                onClick={handleContract}
              >
                Contratar {priceLabel}
              </LoadingButton>
            </Stack>
          </>
        )}
      </Box>
    </DialogAnimate>
  );
}
